import React from 'react';
import { Flame, Leaf, CalendarClock, Utensils } from 'lucide-react';
import { menuUi, wildMenu } from '../data/menuData';

export const VegBadge = ({ level, language }) => (
  <span
    className="inline-flex items-center gap-0.5 align-middle"
    title={level === 'vegan' ? menuUi.vegan[language] : menuUi.veg[language]}
    data-testid={`badge-${level}`}
  >
    {Array.from({ length: level === 'vegan' ? 2 : 1 }).map((_, i) => (
      <span
        key={i}
        className="w-[18px] h-[18px] rounded-full border border-emerald-400/70 text-emerald-400 text-[10px] font-bold flex items-center justify-center leading-none"
      >
        V
      </span>
    ))}
  </span>
);

export const SpicyBadge = ({ level, language }) => (
  <span
    className="inline-flex items-center align-middle"
    title={menuUi[`spicy${level}`][language]}
    data-testid={`badge-spicy-${level}`}
  >
    {Array.from({ length: level }).map((_, i) => (
      <Flame key={i} size={15} className="text-red-500 fill-red-500/30 -mr-0.5" />
    ))}
  </span>
);

const DishRow = ({ item, language, accent, index }) => {
  const desc = item.desc[language] || (language !== 'it' && item.name[language] !== item.name.it ? item.name[language] : '');
  return (
    <div
      className="group relative py-5 pl-4 pr-1 border-b border-dashed border-white/10 last:border-b-0 transition-colors duration-300 hover:bg-white/[0.03]"
      style={{ animation: `fadeInUp 0.5s ease-out ${Math.min(index, 8) * 55}ms both` }}
      data-testid={`dish-${item.name.it.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index}`}
    >
      <span className={`absolute left-0 top-5 bottom-5 w-0.5 ${accent.bar} scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-300`}></span>
      <div className="flex items-start justify-between gap-3 md:gap-4">
        <div className="flex-1 min-w-0">
          <h4 className="font-serif text-lg md:text-xl font-semibold text-white leading-snug">
            {item.name.it}
            {item.v && <span className="ml-2"><VegBadge level={item.v} language={language} /></span>}
            {item.spicy && <span className="ml-2"><SpicyBadge level={item.spicy} language={language} /></span>}
          </h4>
          {desc && (
            <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{desc}</p>
          )}
          {item.note && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-300/90 bg-amber-300/10 border border-amber-300/20 rounded-full px-3 py-1">
              <CalendarClock size={13} /> {menuUi[item.note][language]}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 text-right space-y-1">
          {item.prices.map((p, i) => (
            <div key={i} className="flex items-baseline justify-end gap-2 whitespace-nowrap">
              {p.label && p.label !== 'p.p.' && (
                <span className="text-white/45 text-xs">({p.label})</span>
              )}
              <span className={`font-serif text-lg md:text-xl font-bold ${accent.text}`}>
                <span className="text-[11px] font-sans font-medium opacity-70 mr-1">Fr.</span>
                {p.value}
                {p.label === 'p.p.' && <span className="text-xs font-sans font-medium opacity-70 ml-1">{menuUi.pernote[language]}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ExtrasBox = ({ extras, language, accent }) => (
  <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.03] p-5 md:p-6" data-testid="menu-extras-box">
    <h4 className={`font-serif italic text-xl mb-3 ${accent.text}`}>{extras.title[language]}</h4>
    <div className="space-y-2">
      {extras.rows.map((r, i) => (
        <React.Fragment key={i}>
          {r.group && (
            <p className="text-white/40 text-xs tracking-[0.2em] uppercase pt-3">{r.group[language]}</p>
          )}
          <div className="flex items-baseline gap-3 text-sm text-white/75">
            <span>{r.name[language]}</span>
            <span className="flex-1 border-b border-dotted border-white/20 translate-y-[-3px]"></span>
            <span className={`font-semibold whitespace-nowrap ${accent.text}`}>Fr. {r.value}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const WildMenuBox = ({ language }) => (
  <div className="mt-8 rounded-xl border border-amber-500/30 bg-gradient-to-br from-[#2a1810] to-[#1c1410] p-6 md:p-8" data-testid="wild-menu-box">
    <div className="text-center mb-6">
      <h4 className="font-serif text-2xl md:text-3xl font-bold text-amber-400">{menuUi.wildMenuTitle[language]}</h4>
      <div className="w-16 h-0.5 bg-amber-500/60 mx-auto mt-3"></div>
    </div>
    <div className="grid md:grid-cols-3 gap-6">
      {wildMenu.courses.map((c, i) => (
        <div key={i} className="text-center md:px-3 md:border-l md:first:border-l-0 border-white/10">
          <p className="text-amber-300 text-xs font-semibold tracking-[0.18em] uppercase mb-2">{c.label[language]}</p>
          <p className="text-white/70 text-sm leading-relaxed">{c.text[language]}</p>
        </div>
      ))}
    </div>
    <div className="text-center mt-6">
      <span className="inline-block border border-amber-500/50 rounded-md px-6 py-2 font-serif text-xl font-bold text-amber-400" data-testid="wild-menu-price">
        Fr. {wildMenu.price}
      </span>
    </div>
  </div>
);

export const Legend = ({ language }) => (
  <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/50" data-testid="menu-legend">
    <span className="inline-flex items-center gap-2"><VegBadge level="veg" language={language} /> {menuUi.veg[language]}</span>
    <span className="inline-flex items-center gap-2"><VegBadge level="vegan" language={language} /> {menuUi.vegan[language]}</span>
    <span className="inline-flex items-center gap-2"><SpicyBadge level={1} language={language} /> {menuUi.spicy1[language]}</span>
    <span className="inline-flex items-center gap-2"><SpicyBadge level={2} language={language} /> {menuUi.spicy2[language]}</span>
    <span className="inline-flex items-center gap-2"><SpicyBadge level={3} language={language} /> {menuUi.spicy3[language]}</span>
  </div>
);

const MenuCategoryPanel = ({ category, language }) => {
  const seasonal = category.seasonal;
  const accent = seasonal
    ? { text: 'text-amber-400', bar: 'bg-amber-400', border: 'border-amber-500/30' }
    : { text: 'text-[#c9a227]', bar: 'bg-[#c9a227]', border: 'border-[#c9a227]/25' };
  const subtitle = language === 'it' ? category.alt.it : category.title.it;
  let counter = 0;

  return (
    <div className={`rounded-2xl border ${accent.border} bg-[#181818] p-5 md:p-10 shadow-2xl`} data-testid={`menu-panel-${category.key}`}>
      <div className="text-center mb-8">
        <h3 className={`font-serif text-3xl md:text-4xl font-bold ${accent.text}`}>{category.title[language]}</h3>
        <p className="text-white/40 text-xs tracking-[0.3em] uppercase mt-2">{subtitle}</p>
        <div className="flex items-center justify-center gap-3 mt-4">
          <span className={`h-px w-14 ${accent.bar} opacity-50`}></span>
          <Utensils size={14} className={accent.text} />
          <span className={`h-px w-14 ${accent.bar} opacity-50`}></span>
        </div>
      </div>

      {category.sideIncluded && (
        <div className="mb-6 flex items-center justify-center gap-3 rounded-lg bg-[#c9a227]/10 border border-[#c9a227]/25 px-4 py-3 text-center" data-testid="side-dish-note">
          <Leaf size={16} className="text-[#c9a227] flex-shrink-0" />
          <p className="text-sm text-white/80">
            <span className="font-semibold text-[#c9a227]">{menuUi.inclSide[language]}:</span> {menuUi.inclSideDesc[language]}
          </p>
        </div>
      )}

      {category.groups.map((group, gi) => (
        <div key={gi} className={gi > 0 ? 'mt-10' : ''}>
          {group.title && (
            <div className="flex items-center gap-4 mb-2">
              <h4 className={`font-serif italic text-xl ${accent.text}`}>{group.title[language]}</h4>
              <span className="flex-1 h-px bg-white/10"></span>
            </div>
          )}
          <div>
            {group.items.map((item) => (
              <DishRow key={`${item.name.it}-${counter}`} item={item} language={language} accent={accent} index={counter++} />
            ))}
          </div>
        </div>
      ))}

      {category.extras && <ExtrasBox extras={category.extras} language={language} accent={accent} />}

      {seasonal && <WildMenuBox language={language} />}

      {seasonal && (
        <p className="text-center text-white/40 text-xs mt-6" data-testid="wild-origin">
          {menuUi.wildOrigin[language]}: {menuUi.wildOriginText[language]}
        </p>
      )}

      <Legend language={language} />
    </div>
  );
};

export default MenuCategoryPanel;
